import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import DashboardLayout from '@/components/layout/DashboardLayout';
import apiService from '../services/api';
import { useToast } from '@/hooks/use-toast';
import { Download, ArrowLeft, AlertCircle, Loader2, CreditCard } from 'lucide-react';

interface ServiceRequestDetail {
  id: string;
  service_category: string;
  property_type: string;
  service_type: string;
  contact_email: string;
  contact_phone: string;
  contact_name: string;
  company?: string;
  details: Record<string, any>;
  status: 'pending' | 'quoted' | 'accepted' | 'rejected' | 'paid';
  payment_status?: 'pending' | 'completed' | 'failed';
  quoted_price?: number;
  created_at: string;
  updated_at: string;
}

export default function ServiceRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [request, setRequest] = useState<ServiceRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchServiceRequest();
    
    // Check if returning from payment
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      const sessionId = params.get('session');
      // Verify payment with backend
      verifyPayment(sessionId);
      // Re-fetch data after a short delay to ensure DB is updated
      setTimeout(() => {
        fetchServiceRequest();
      }, 500);
    }
  }, [id]);

  const fetchServiceRequest = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.request(`/service-requests/${id}`, 'GET');

      if (response.success) {
        setRequest(response.data);
      } else {
        setError(response.message || 'Failed to load service request');
      }
    } catch (err: any) {
      console.error('Error fetching service request:', err);
      setError(err.message || 'An error occurred while loading the request');
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (sessionId: string | null) => {
    if (!id || !sessionId) return;

    try {
      const response = await apiService.request('/service-requests/payment/verify', 'POST', {
        serviceRequestId: id,
        sessionId
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Payment completed successfully!'
        });
        // Data will be refreshed by the setTimeout in useEffect
      } else {
        console.error('Payment verification failed:', response.message);
      }
    } catch (err: any) {
      console.error('Error verifying payment:', err);
    }
  };

  const handlePayNow = () => {
    if (!request?.id) return;
    try {
      // Navigate to payment page for this service request
      navigate(`/dashboard/service-requests/${request.id}/payment`);
    } catch (err) {
      console.error('Payment navigation error:', err);
      toast({
        title: 'Error',
        description: 'Could not proceed to payment',
        variant: 'destructive',
      });
    }
  };

  const downloadPDF = async () => {
    if (!id) return;

    try {
      setDownloading(true);
      console.log('[PDF Download] Starting download for request:', id);
      
      // Fetch the PDF
      const response = await fetch(`/api/service-requests/${id}/pdf/download`);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('[PDF Download] Error response:', errorData);
        toast({ 
          title: 'Error', 
          description: errorData.message || 'Failed to download PDF',
          variant: 'destructive' 
        });
        return;
      }
      
      // Check if we got a PDF
      const contentType = response.headers.get('content-type');
      console.log('[PDF Download] Content-Type:', contentType);
      
      if (!contentType || !contentType.includes('application/pdf')) {
        console.error('[PDF Download] Invalid content type:', contentType);
        toast({ 
          title: 'Error', 
          description: 'Server did not return a valid PDF',
          variant: 'destructive' 
        });
        return;
      }
      
      // Convert response to blob
      const blob = await response.blob();
      console.log('[PDF Download] Received blob, size:', blob.size, 'bytes');
      
      if (blob.size === 0) {
        console.error('[PDF Download] Empty PDF blob');
        toast({ 
          title: 'Error', 
          description: 'PDF is empty',
          variant: 'destructive' 
        });
        return;
      }
      
      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Get filename from Content-Disposition header or use default
      const disposition = response.headers.get('content-disposition');
      let filename = 'service-request.pdf';
      if (disposition) {
        const matches = disposition.match(/filename="([^"]+)"/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      console.log('[PDF Download] ✅ PDF downloaded successfully:', filename);
      toast({ 
        title: 'Success', 
        description: 'PDF downloaded successfully' 
      });
    } catch (err: any) {
      console.error('[PDF Download] Error:', err);
      toast({ 
        title: 'Error', 
        description: err.message || 'Failed to download PDF', 
        variant: 'destructive' 
      });
    } finally {
      setDownloading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'quoted':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'paid':
        return 'bg-emerald-100 text-emerald-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (paymentStatus?: string) => {
    switch (paymentStatus?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price?: number | string) => {
    if (!price) return 'Not quoted';
    return '£' + parseFloat(String(price)).toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading service request...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="bg-white rounded-lg shadow-lg p-8 border-l-4 border-red-500">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
                <p className="text-gray-600">{error || 'Service request not found'}</p>
                <Button className="mt-4" onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>Service Request {request?.id} - Virewire</title>
      </Helmet>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard/service-requests')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Requests
            </Button>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Title Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 md:px-8 py-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Service Request</h1>
                <p className="text-blue-100 font-mono text-sm">{request.id}</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <Badge className={getStatusColor(request.status)}>
                  {request.status?.charAt(0).toUpperCase() + request.status?.slice(1)}
                </Badge>
                {request.payment_status && (
                  <Badge className={getPaymentStatusColor(request.payment_status)}>
                    {request.payment_status === 'completed' ? '✓ Paid' : request.payment_status?.charAt(0).toUpperCase() + request.payment_status?.slice(1)}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Service Details */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Service Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Category</label>
                  <p className="text-lg text-gray-900 capitalize">{request.service_category?.replace('-', ' ')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Property Type</label>
                  <p className="text-lg text-gray-900 capitalize">{request.property_type?.replace('-', ' ')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Service Type</label>
                  <p className="text-lg text-gray-900 capitalize">{request.service_type?.replace('-', ' ')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Quoted Price</label>
                  <p className="text-lg text-gray-900 font-semibold">{formatPrice(request.quoted_price)}</p>
                  <p className="text-sm text-gray-500 mt-1">+ 20% VAT: £{request.quoted_price ? (parseFloat(String(request.quoted_price)) * 0.2).toFixed(2) : '0.00'}</p>
                  <p className="text-sm font-semibold text-blue-600 mt-2">Total: £{request.quoted_price ? (parseFloat(String(request.quoted_price)) * 1.2).toFixed(2) : '0.00'}</p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
                  <p className="text-lg text-gray-900">{request.contact_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                  <p className="text-lg text-gray-900 break-all">{request.contact_email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Phone</label>
                  <p className="text-lg text-gray-900">{request.contact_phone}</p>
                </div>
                {request.company && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Company</label>
                    <p className="text-lg text-gray-900">{request.company}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Details */}
            {request.details && Object.keys(request.details).length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Additional Details</h2>
                <div className="bg-gray-50 rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(request.details).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-600 mb-1 capitalize">
                        {key.replace(/_/g, ' ')}
                      </label>
                      <p className="text-gray-900">
                        {Array.isArray(value) ? value.join(', ') : String(value)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Timeline</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">Submitted:</span>
                  <span className="font-medium text-gray-900">{formatDate(request.created_at)}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="font-medium text-gray-900">{formatDate(request.updated_at)}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
              {request.status === 'quoted' && request.quoted_price && (
                <Button
                  onClick={handlePayNow}
                  className="gap-2 bg-emerald-700 hover:bg-emerald-800"
                >
                  <CreditCard className="w-4 h-4" />
                  Pay Now - £{(parseFloat(String(request.quoted_price)) * 1.2).toFixed(2)}
                </Button>
              )}
              {request.status === 'paid' && (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <span className="text-green-700 font-semibold">✓ Payment Complete</span>
                </div>
              )}
              <Button
                onClick={downloadPDF}
                disabled={downloading}
                className="gap-2"
                variant={request.status === 'quoted' && request.quoted_price && request.status !== 'paid' ? 'outline' : 'default'}
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download PDF
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
